import { useState } from "react";
import { useMutation } from "convex/react";
import { anyApi } from "convex/server";

export interface RentSummary {
  leaseId: string;
  isLandlord: boolean;
  monthlyRentCents: number;
  leaseStartDate: string;
  leaseEndDate: string | null;
  leaseIsActive: boolean;
  periods: { periodKey: string; isPaid: boolean; paidAt: number | null }[];
}

// Reusable per-lease rent ledger UI. Used by both MyRentSection
// (tenant dashboard, read-only) and LeaseRentManager (landlord view
// on /tenants, with inline toggle). The data shape is identical;
// landlord caps come from `summary.isLandlord` returned by Convex.
export function RentLedger({ summary }: { summary: RentSummary | null }) {
  const markPaid = useMutation(anyApi.rent.markPaid);
  const markUnpaid = useMutation(anyApi.rent.markUnpaid);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (summary === null) {
    return (
      <div className="px-6 py-8 text-center">
        <p className="text-sm font-medium text-slate-900">
          No active lease.
        </p>
        <p className="mt-1 text-sm text-slate-500">
          Once a lease is set up, monthly rent status will appear here.
        </p>
      </div>
    );
  }

  if (summary.periods.length === 0) {
    return (
      <div className="px-6 py-8 text-center">
        <p className="text-sm text-slate-500">
          Lease starts {formatPeriod(summary.leaseStartDate.slice(0, 7))} —
          first month will appear here when it begins.
        </p>
      </div>
    );
  }

  // Reverse chronological — current month at the top.
  const periods = [...summary.periods].reverse();

  const handleToggle = async (periodKey: string, isPaid: boolean) => {
    setBusyKey(periodKey);
    setError(null);
    try {
      if (isPaid) {
        await markUnpaid({ leaseId: summary.leaseId, periodKey });
      } else {
        await markPaid({ leaseId: summary.leaseId, periodKey });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyKey(null);
    }
  };

  const dollars = (summary.monthlyRentCents / 100).toFixed(0);

  return (
    <div className="px-6 py-4">
      <p className="text-xs text-slate-500">
        Monthly rent:{" "}
        <span className="text-slate-900 font-medium">${dollars}</span>
        {" · "}
        {summary.leaseIsActive
          ? `Lease since ${formatPeriod(summary.leaseStartDate.slice(0, 7))}`
          : `Lease closed ${summary.leaseEndDate ? formatPeriod(summary.leaseEndDate.slice(0, 7)) : ""}`}
      </p>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
      <ul className="mt-3 divide-y divide-slate-100">
        {periods.map((p) => (
          <li
            key={p.periodKey}
            className="py-3 flex items-center justify-between gap-3 first:pt-0"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900">
                {formatPeriod(p.periodKey)}
              </p>
              <p className="mt-0.5 text-xs text-slate-500">
                {p.isPaid && p.paidAt
                  ? `Paid ${formatTimestamp(p.paidAt)}`
                  : "Unpaid"}
              </p>
            </div>
            {summary.isLandlord ? (
              <button
                type="button"
                onClick={() => handleToggle(p.periodKey, p.isPaid)}
                disabled={busyKey === p.periodKey}
                className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset transition-colors disabled:opacity-50 ${
                  p.isPaid
                    ? "bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100"
                    : "bg-slate-100 text-slate-600 ring-slate-200 hover:bg-slate-200"
                }`}
                title={
                  p.isPaid ? "Click to mark unpaid" : "Click to mark paid"
                }
              >
                {busyKey === p.periodKey ? "…" : p.isPaid ? "Paid" : "Unpaid"}
              </button>
            ) : (
              <span
                className={`shrink-0 inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset ${
                  p.isPaid
                    ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                    : "bg-slate-100 text-slate-600 ring-slate-200"
                }`}
              >
                {p.isPaid ? "Paid" : "Unpaid"}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatPeriod(periodKey: string): string {
  // periodKey = YYYY-MM
  const [y, m] = periodKey.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

function formatTimestamp(ts: number): string {
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
